import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import React, { FC } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { CartLineModel, ProductModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { TYPOGRAPHY } from "@store/theme/design";
import { reduceCurrency } from "@store/utilities/pricingHelpers";
import { connect } from "react-redux";
/* Store Custom Code - End */

/* Store Custom Code - Start */
const mapStateToProps = (state: ApplicationState) => {
    return {
        language: state?.context?.session?.language || undefined,
        currency: state?.context?.session?.currency || undefined,
    };
};
/* Store Custom Code - End */

interface OwnProps {
    extendedStyles?: ProductQuantityBreakPricingStyles;
    /* Store Custom Code */
    isModal?: boolean;
    useQuantityPromotionMessage?: boolean;
    productLine?: CartLineModel;
}

export interface ProductQuantityBreakPricingStyles {
    viewLink?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    /* Store Custom Code */
    typography?: TypographyPresentationProps;
    tablePlp?: DataTableProps;
    tableHeader?: TypographyPresentationProps;
}

export const productQuantityBreakPricingStyles: ProductQuantityBreakPricingStyles = {
    // Store Custom Code
    modal: {
        size: 500, // Store Custom Code
        cssOverrides: {
            modalContent: css`
                padding: 0;
                margin: 0px 10px 0px 10px; /* Store Custom Code */
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    /* Store Custom Code - Start */
    tablePlp: {
        css: css`
            width: 100%;
            border: 1px solid #edeff3;
            margin-bottom: 10px;
            margin-top: 10px;
        `,
    },
    table: {
        css: css`
            width: 50%;
            border: 1px solid #edeff3;
            margin-bottom: 10px;
            margin-top: 10px;
        `,
    },
    tableHeader: {
        css: css`
            font-weight: 700;
        `,
    },
    viewLink: {
        variant: "tertiary",
        sizeVariant: "small",
        css: css`
            padding: 0.5em 1em;
            width: auto;

            &:not(:disabled):hover {
                background: #f6f6f6 !important; // Override regular button styles
            }

            &,
            span {
                font-size: ${TYPOGRAPHY.size.smaller};
            }
        `,
    },
    breakQtyHeader: {
        css: css`
            padding: 0px 20px;
        `,
    },
    /* Store Custom Code - End */
    breakPriceHeader: { alignX: "center" },
    savingsHeader: { alignX: "center" },
    breakQtyCell: { alignX: "center" },
    breakPriceCell: { alignX: "center" },
    savingsCell: { alignX: "center" },
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps; // Store Custom Code

// Store Custom Code - This component now receives the `isModal` parameter. When it is true, use vanilla code. Else, display the table directly.
const ProductQuantityBreakPricing: FC<OwnProps & HasProduct & Props> = ({
    product,
    extendedStyles,
    /* Store Custom Code */
    productInfo, // See: let pricing: ProductPriceDto | undefined | null;
    productLine,
    isModal,
    useQuantityPromotionMessage,
    currency,
    language,
}) => {
    const showPricing = useAppSelector(state => state.context.showPricing);
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewLinkClickHandler = () => {
        setModalIsOpen(true);
    };

    const [styles] = React.useState(() => mergeToNew(productQuantityBreakPricingStyles, extendedStyles));

    /* Store custom code - Make component compatible on cart */
    let currentProduct: CartLineModel | ProductModel;
    let pricing: ProductPriceDto | undefined | null;
    if (productLine !== undefined && productLine !== null) {
        pricing = productLine?.pricing;
        currentProduct = productLine;
    } else {
        pricing = productInfo?.pricing;
        currentProduct = product;
    }

    if (
        !currentProduct ||
        currentProduct.quoteRequired ||
        !pricing ||
        !pricing.unitRegularBreakPrices ||
        pricing.unitRegularBreakPrices.length < 2 ||
        !showPricing
    ) {
        return null;
    }

    /* Store custom code */
    const translateSave = translate("Save");
    const translateReplacement = translate("savePriceBreak");
    const breakPricesArray = [...pricing!.unitRegularBreakPrices];
    const maxBreak = breakPricesArray.pop();
    const corePrice = pricing?.additionalResults?.["corePriceDecimal"] ?? "0"; // Store Custom Code
    const hasCorePrice = corePrice != null; // Store Custom Code

    const discountMessage = maxBreak
        ? maxBreak?.savingsMessage.replace(translateSave, translateReplacement).toLocaleLowerCase()
        : "";

    let labelModalButton = translate("Qty break available: {0}", discountMessage);

    /* Store Custom Code - Start */
    const getBreakPriceAdjusted = (breakPrice: number) => {
        return reduceCurrency(breakPrice, corePrice, language, currency);
    };
    /* Store Custom Code - End */

    if (
        productLine !== undefined &&
        productLine !== null &&
        useQuantityPromotionMessage === true &&
        productLine?.qtyOrdered !== null &&
        productLine?.qtyOrdered > 0 &&
        productLine?.pricing
    ) {
        const currentQuantity = productLine?.qtyOrdered;
        if (maxBreak) {
            const neededForBreak = maxBreak?.breakQty - currentQuantity;
            labelModalButton =
                neededForBreak > 0
                    ? translate("Add {0} more to {1}", String(neededForBreak), discountMessage)
                    : translate("View Quantity Break Pricing"); // Store Custom Code
        }
    }

    return (
        <>
            {/* Store custom code */}
            {isModal ? (
                <>
                    <Button
                        {...styles.viewLink}
                        onClick={viewLinkClickHandler}
                        data-test-selector="quantityBreakPricingLink"
                    >
                        {labelModalButton}
                    </Button>
                    <Modal
                        {...styles.modal}
                        headline={translate("Quantity Pricing")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                        data-test-selector="quantityBreakPricingModal"
                    >
                        <DataTable {...styles.tablePlp}>
                            <DataTableHead {...styles.tableHead}>
                                <DataTableHeader {...styles.breakQtyHeader}>{translate("Min Qty")}</DataTableHeader>
                                <DataTableHeader {...styles.breakPriceHeader}>{translate("Price Per")}</DataTableHeader>
                                <DataTableHeader {...styles.savingsHeader} title={translate("Savings")} />
                            </DataTableHead>
                            <DataTableBody {...styles.tableBody}>
                                {pricing!.unitRegularBreakPrices!.map(breakPrice => (
                                    <DataTableRow
                                        {...styles.tableRow}
                                        key={breakPrice.breakQty}
                                        data-test-selector="breakPriceRow"
                                    >
                                        <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                            {breakPrice.breakQty}
                                        </DataTableCell>
                                        <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                            {breakPrice.breakPriceDisplay}
                                        </DataTableCell>
                                        <DataTableCell {...styles.savingsCell} data-test-selector="message">
                                            {breakPrice.savingsMessage.replace(translateSave, translateReplacement)}
                                        </DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </Modal>
                </>
            ) : (
                <>
                    <Typography {...styles.tableHeader}>{translate("Quantity Pricing")}</Typography>
                    <DataTable {...styles.table}>
                        <DataTableHead {...styles.tableHead}>
                            <DataTableHeader {...styles.breakQtyHeader}>{translate("Min Qty")}</DataTableHeader>
                            <DataTableHeader {...styles.breakPriceHeader}>{translate("Price Per")}</DataTableHeader>
                            <DataTableHeader {...styles.savingsHeader} title={translate("Savings")} />
                        </DataTableHead>
                        <DataTableBody {...styles.tableBody}>
                            {pricing!.unitRegularBreakPrices!.map(breakPrice => (
                                <DataTableRow
                                    {...styles.tableRow}
                                    key={breakPrice.breakQty}
                                    data-test-selector="breakPriceRow"
                                >
                                    <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                        {breakPrice.breakQty}
                                    </DataTableCell>
                                    <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                        {
                                            hasCorePrice
                                                ? getBreakPriceAdjusted(breakPrice.breakPrice)
                                                : breakPrice.breakPriceDisplay /* Store Custom Code */
                                        }
                                    </DataTableCell>
                                    <DataTableCell {...styles.savingsCell} data-test-selector="message">
                                        {breakPrice.savingsMessage.replace(translateSave, translateReplacement)}
                                    </DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </>
            )}
        </>
    );
};

export default withProduct(connect(mapStateToProps)(ProductQuantityBreakPricing)); // Store Custom Code
